import { sellar } from "./quien";
import { supabase } from "../supabase/SupabaseClient";

// Firestore permitía guardar arrays/objetos anidados dentro de un campo
// (ej: depositos.stock, personas.periodos, eventos.chequeos...). Al migrar
// a Supabase, esas columnas son "text" con el JSON adentro como string.
// Estas dos funciones hacen la conversión automática en ambas direcciones,
// sin tener que mapear campo por campo en cada service.

const parseJSONFields = (row) => {
  const resultado = {};

  for (const [key, value] of Object.entries(row)) {
    if (
      typeof value === "string" &&
      (value.startsWith("[") || value.startsWith("{")) &&
      (value.endsWith("]") || value.endsWith("}"))
    ) {
      try {
        resultado[key] = JSON.parse(value);
        continue;
      } catch {
        // no era JSON de verdad, se deja como string tal cual
      }
    }
    resultado[key] = value;
  }

  return resultado;
};

const stringifyJSONFields = (data) => {
  const resultado = {};

  for (const [key, value] of Object.entries(data)) {
    if (value instanceof Date) {
      resultado[key] = value.toISOString();
    } else if (value !== null && typeof value === "object") {
      resultado[key] = JSON.stringify(value);
    } else {
      resultado[key] = value;
    }
  }

  return resultado;
};

/**
 * Se suscribe a una tabla de Supabase: carga todo una vez y después
 * escucha cambios en tiempo real (insert/update/delete) para mantener
 * el listado actualizado — mismo rol que tenía el onSnapshot de Firestore.
 *
 * @param {string} table - nombre de la tabla en Supabase
 * @param {(rows: object[]) => void} callback - se llama con el listado completo cada vez que cambia
 * @returns {() => void} función para des-suscribirse
 */
let contadorCanal = 0;
const PAGINA = 1000; // el tope de PostgREST por respuesta

export const subscribeTable = (table, callback, options = {}) => {
  const { orderBy = null, ascending = false, limit = null, filter = null } =
    options;
  let rows = [];
  let cancelado = false;
  const nombreCanal = `realtime:${table}:${++contadorCanal}`;

  /* ── SE LEE DE A PÁGINAS, PORQUE PostgREST CORTA EN 1.000 Y NO AVISA ─────
   *
   * Un `select("*")` sin `range` devuelve las primeras 1.000 filas y un 200:
   * no hay error ni aviso. `personas` tiene 2.479 (356 activas) y `stock`
   * 1.840, así que desde que pasaron las mil, 1.479 personas y 840 artículos
   * no existían para el Hub: el buscador no encontraba a quien ya no trabaja
   * (Igna, 02/10) y, como la consulta no tenía orden, tampoco a cualquier
   * activo que hubiera quedado fuera de esa tajada.
   *
   * Con `limit` el que llama pidió un tope, y se respeta tal cual. Sin
   * `limit` se pide página por página, ordenado también por `id` para que el
   * corte entre páginas sea estable (sin orden, una fila puede aparecer dos
   * veces o ninguna). Si una página falla, NO se entrega media tabla: media
   * tabla se ve igual que la tabla entera y miente sin que nadie lo note.
   * Mismo criterio que `_cargado` en FlotaBot (planilla_ingesta). */
  const cargar = async () => {
    const base = () => {
      let q = supabase.from(table).select("*");
      if (filter) q = filter(q);
      /* `nullsFirst: false`: en Postgres un orden descendente pone los
       * vacíos ARRIBA. En `eventos` hay 180 filas sin `orden_num` (las de la
       * reconstrucción de septiembre, con `orden` «22350.5»): encabezaban el
       * registro de portería y, con el tope, empujaban afuera las de hoy
       * (ticket 420). */
      if (orderBy) q = q.order(orderBy, { ascending, nullsFirst: false });
      return q;
    };

    let data = [];
    if (limit) {
      const r = await base().limit(limit);
      if (r.error) {
        console.error(`[Supabase] Error cargando ${table}:`, r.error.message);
        return;
      }
      data = r.data || [];
    } else {
      for (let desde = 0; ; desde += PAGINA) {
        let q = base();
        if (orderBy !== "id") q = q.order("id", { ascending: true });
        const r = await q.range(desde, desde + PAGINA - 1);
        if (r.error) {
          console.error(`[Supabase] Error cargando ${table} (desde ${desde}):`, r.error.message);
          return;
        }
        const pagina = r.data || [];
        data = data.concat(pagina);
        if (pagina.length < PAGINA || cancelado) break;
      }
    }

    if (cancelado) return;
    rows = data.map(parseJSONFields);
    callback(rows);
  };

  cargar();

  const reordenar = () => {
    if (!orderBy) return;
    rows = [...rows].sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      const cmp = String(av ?? "").localeCompare(String(bv ?? ""), "es", {
        numeric: true,
      });
      return ascending ? cmp : -cmp;
    });
    if (limit) rows = rows.slice(0, limit);
  };

  const channel = supabase
    .channel(nombreCanal)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table },
      (payload) => {
        if (payload.eventType === "INSERT") {
          const nuevo = parseJSONFields(payload.new);
          rows = [...rows.filter((r) => r.id !== nuevo.id), nuevo];
          reordenar();
        } else if (payload.eventType === "UPDATE") {
          const actualizado = parseJSONFields(payload.new);
          rows = rows.map((r) => (r.id === actualizado.id ? actualizado : r));
          reordenar();
        } else if (payload.eventType === "DELETE") {
          rows = rows.filter((r) => r.id !== payload.old.id);
        }
        callback(rows);
      },
    )
    .subscribe();

  return () => {
    cancelado = true;
    supabase.removeChannel(channel);
  };
};

export const getAllRows = async (table) => {
  const { data, error } = await supabase.from(table).select("*");
  if (error) throw error;
  return (data || []).map(parseJSONFields);
};

export const getRow = async (table, id) => {
  const { data, error } = await supabase
    .from(table)
    .select("*")
    .eq("id", String(id))
    .maybeSingle();
  if (error) throw error;
  return data ? parseJSONFields(data) : data;
};

/* EL AUTOR SE ESCRIBE ACÁ Y NO EN CADA FORMULARIO. Éste es el cuello por el
   que pasa toda la escritura del hub: repartido por las pantallas, la próxima
   que se agregue nacería sin autor y nadie se enteraría hasta que el dato
   haga falta. Ver `services/quien.js`. */
/* ── LA CLAVE NO SIEMPRE SE LLAMA `id` ──────────────────────────────────
 *
 * Casi todas las tablas del hub vienen de Firestore y tienen `id`, así que
 * estaba escrito a mano en el `.eq()`. `chofer_documentos` NO: nació acá y su
 * clave es `dni`.
 *
 * Y eso no fallaba de una forma visible — fallaba de la peor: PostgREST
 * rechaza `.eq("id", …)` sobre una tabla sin esa columna, así que **ninguna
 * corrección hecha desde la pantalla de Documentos entró nunca**. Las fechas se
 * editaban, el campo se ponía en «guardando», volvía el error y el valor
 * anterior. Los **0 de 224 autores** de esa tabla son la huella: la columna
 * existe, la pantalla la dibuja, y nunca se escribió una fila.
 *
 * Va como parámetro y no como una segunda función: repartido, el próximo
 * `updateRowPorLoQueSea` nace sin sellar el autor, que es exactamente lo que
 * este cuello viene a evitar.
 */
export const createRow = async (table, id, data, clave = "id") => {
  const payload = stringifyJSONFields(sellar(table, data));
  if (id !== undefined && id !== null) payload[clave] = String(id);

  const { data: row, error } = await supabase
    .from(table)
    .insert(payload)
    .select()
    .single();
  if (error) throw error;
  return parseJSONFields(row);
};

export const updateRow = async (table, id, data, clave = "id") => {
  const { data: row, error } = await supabase
    .from(table)
    .update(stringifyJSONFields(sellar(table, data)))
    .eq(clave, String(id))
    .select()
    .single();
  if (error) throw error;
  return parseJSONFields(row);
};

export const deleteRow = async (table, id, clave = "id") => {
  const { error } = await supabase.from(table).delete().eq(clave, String(id));
  if (error) throw error;
};
